"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
function _export(target, all) {
    for(var name in all)Object.defineProperty(target, name, {
        enumerable: true,
        get: Object.getOwnPropertyDescriptor(all, name).get
    });
}
_export(exports, {
    get alertKeys () {
        return alertKeys;
    },
    get alerts () {
        return alerts;
    },
    get birthDate () {
        return birthDate;
    },
    get optionalPhilhealthPin () {
        return optionalPhilhealthPin;
    },
    get optionalPhilsysNumber () {
        return optionalPhilsysNumber;
    },
    get optionalText () {
        return optionalText;
    },
    get phMobile () {
        return phMobile;
    },
    get requiredName () {
        return requiredName;
    }
});
const _zod = require("zod");
const _identifiers = require("./identifiers");
const _patientalert = require("./patient-alert");
const _patientlist = require("./patient-list");
/**
 * Field builders shared by patient registration (`patient-create.ts`) and
 * the demographics edit (`patient-update.ts`), so both paths apply the same
 * rules and show the same messages.
 *
 * Every field here is PHI. Issue messages are fixed strings and never
 * interpolate the submitted value, so a 400 body can't echo PHI back.
 */ /** Plausibility ceiling for a date of birth. */ const MAX_AGE_YEARS = 130;
/** The clinic's calendar: "today" for date-of-birth checks. */ const CLINIC_TIME_ZONE = 'Asia/Manila';
function clinicToday() {
    // en-CA formats as YYYY-MM-DD.
    return new Intl.DateTimeFormat('en-CA', {
        timeZone: CLINIC_TIME_ZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    }).format(new Date());
}
function yearsBefore(isoDate, years) {
    const year = Number(isoDate.slice(0, 4)) - years;
    return `${String(year).padStart(4, '0')}${isoDate.slice(4)}`;
}
const requiredName = (max, label)=>_zod.z.string({
        error: `${label} is required.`
    }).trim().min(1, {
        error: `${label} is required.`
    }).max(max, {
        error: `${label} must be at most ${String(max)} characters.`
    });
const optionalText = (max, label)=>_zod.z.string().trim().max(max, {
        error: `${label} must be at most ${String(max)} characters.`
    }).optional().transform((value)=>value === '' ? undefined : value);
const phMobile = (label)=>_zod.z.string({
        error: `${label} is required.`
    }).transform((value, ctx)=>{
        const normalized = (0, _identifiers.normalizePhMobile)(value);
        if (normalized === null) {
            ctx.addIssue({
                code: 'custom',
                message: `Enter a valid Philippine mobile number (09XX XXX XXXX).`
            });
            return _zod.z.NEVER;
        }
        return normalized;
    });
const optionalPhilhealthPin = _zod.z.string().optional().transform((value, ctx)=>{
    const raw = value?.trim() ?? '';
    if (raw === '') {
        return undefined;
    }
    const pin = (0, _identifiers.toCanonicalPhilhealthPin)(raw);
    if (pin === null) {
        ctx.addIssue({
            code: 'custom',
            message: 'Enter a 12-digit PhilHealth PIN.'
        });
        return _zod.z.NEVER;
    }
    return pin;
}).pipe(_patientlist.PhilHealthPinSchema.optional());
const optionalPhilsysNumber = _zod.z.string().optional().transform((value, ctx)=>{
    const raw = value?.trim() ?? '';
    if (raw === '') {
        return undefined;
    }
    const digits = (0, _identifiers.normalizePhilsysNumber)(raw);
    if (digits === null) {
        ctx.addIssue({
            code: 'custom',
            message: 'Enter a 16-digit PhilSys number.'
        });
        return _zod.z.NEVER;
    }
    return digits;
});
const birthDate = _zod.z.string({
    error: 'Date of birth is required.'
}).date({
    error: 'Enter a valid date of birth.'
}).superRefine((value, ctx)=>{
    // Zod v4 runs refinements after a failed format check; the format
    // issue is already reported.
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return;
    }
    const today = clinicToday();
    if (value > today) {
        ctx.addIssue({
            code: 'custom',
            message: 'Date of birth cannot be in the future.'
        });
        return;
    }
    if (value <= yearsBefore(today, MAX_AGE_YEARS)) {
        ctx.addIssue({
            code: 'custom',
            message: 'Enter a valid date of birth.'
        });
    }
});
const alertKeys = _zod.z.array(_patientalert.PatientAlertSchema, {
    error: 'Select valid alerts.'
}).refine((keys)=>new Set(keys).size === keys.length, {
    error: 'Alerts must be unique.'
});
const alerts = alertKeys.default([]);
